<script setup lang="ts">
import {
  LAWYER_TYPE_LAWYER,
  LAWYER_TYPE_ADVOCATE,
} from '@/shared/constants/lawyerTypeConstants';

import {
  ORGANIZATION_TYPE_CLUB,
  ORGANIZATION_TYPE_BUREAU,
} from '@/shared/constants/organizationTypeConstants';

import { VERIFICATED_LAWYER_STATUS } from '@/shared/constants/moderationConstants';
import { declination } from '@/shared/utils/declination';
import VerificationTooltip from '@/widgets/LawyerCard/ui/Tooltips/VerificationTooltip.vue';
import type { ILawyerCardExperienceProps } from '@/widgets/LawyerCard/model/types/lawyerCard';
import type { ILawyerCardInfo } from '@/entities/lawyer/model/types/lawyer';

withDefaults(defineProps<ILawyerCardExperienceProps>(), {
  isLawyerCompanyCard: false,
  isLawyerDetail: false,
  isArticle: false,
});

const isMobile: boolean = useIsMobile();
const lawyerInfo = inject<ILawyerCardInfo>('lawyerInfo');
const isShowExperience = inject<boolean>('isShowExperience', true);

const isShowTooltip: Ref<boolean> = ref(false);

const isVerificated: ComputedRef<boolean> = computed(
  () => lawyerInfo?.is_validate === VERIFICATED_LAWYER_STATUS,
);

const isCompany: ComputedRef<boolean> = computed(
  () =>
    (lawyerInfo?.role === LAWYER_TYPE_LAWYER ||
      lawyerInfo?.role === LAWYER_TYPE_ADVOCATE) &&
    Boolean(lawyerInfo?.company_id),
);

const lawyerCardType: ComputedRef<string> = computed(() => {
  const role = lawyerInfo?.role;
  const companyId = lawyerInfo?.company_id;

  if (role === LAWYER_TYPE_LAWYER && !companyId) return 'Юрист';
  if (role === LAWYER_TYPE_LAWYER && companyId) return 'Юридическая компания';
  if (role === LAWYER_TYPE_ADVOCATE && !companyId) return 'Адвокат';
  if (role === LAWYER_TYPE_ADVOCATE && companyId) {
    if (lawyerInfo?.company?.organisation_type === ORGANIZATION_TYPE_CLUB) {
      return 'Адвокатская коллегия';
    }
    if (lawyerInfo?.company?.organisation_type === ORGANIZATION_TYPE_BUREAU) {
      return 'Адвокатское бюро';
    }
    return 'Адвокатская контора';
  }
  return '';
});

const lawyerExperienceType: ComputedRef<string> = computed(() =>
  isCompany.value ? 'Год основания:' : 'Опыт:',
);

const lawyerExperienceNumber: ComputedRef<string> = computed(() => {
  const currentYear = new Date().getFullYear();
  const activityDate = new Date(lawyerInfo?.activity_date ?? '');
  const yearActivity = activityDate.getFullYear();
  const years = currentYear - yearActivity;

  return isCompany.value
    ? `${yearActivity} год`
    : `${years} ${declination(years, [' год', ' года', ' лет'])}`;
});

const showTooltip = (): void => {
  isShowTooltip.value = true;
};

const hideTooltip = (): void => {
  isShowTooltip.value = false;
};
</script>

<template>
  <div
    :class="[
      'lawyer-card-experience',
      { _mobile: isMobile },
      { _company: isCompany },
      { _article: isArticle },
      { _detail: isLawyerDetail },
      { '_is-lawyer-company': isLawyerCompanyCard },
    ]"
  >
    <div class="lawyer-card-experience__type-wrapper">
      <span class="lawyer-card-experience__type">
        {{ lawyerCardType }}
      </span>
      <div
        class="lawyer-card-experience__icon-wrapper"
        @mouseover="showTooltip"
        @mouseleave="hideTooltip"
      >
        <svg
          v-if="isVerificated"
          class="app-icon lawyer-card-experience__verification-icon"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          aria-hidden="true"
        >
          <use href="#icon-verification" />
        </svg>
        <VerificationTooltip
          v-model="isShowTooltip"
          class="lawyer-card-experience__tooltip"
        />
      </div>
    </div>
    <div v-if="isShowExperience" class="lawyer-card-experience__count">
      {{ lawyerExperienceType }}
      <span class="lawyer-card-experience__years">{{ lawyerExperienceNumber }}</span>
    </div>
  </div>
</template>

<style lang="less">
.lawyer-card-experience {
  &__type-wrapper {
    display: inline-flex;
    align-items: center;
    margin-right: @size-x2;
  }

  &._is-lawyer-company &__type-wrapper {
    margin-right: 0;
  }

  &__icon-wrapper {
    display: inline-flex;
    position: relative;
    margin-left: 6px;
    margin-bottom: -1px;
  }

  &__verification-icon {
    width: 14px;
    height: 14px;
  }

  &._is-lawyer-company &__years {
    font-weight: 600;
  }

  &._company {
    display: flex;
    flex-direction: column;
  }

  &__count {
    margin-top: @main-size;
  }

  &._company &__count {
    display: block;
  }

  &._article &__tooltip {
    left: -65px;

    @media @tablet {
      left: -120px;
    }
  }

  &._mobile {
    align-items: center;
  }

  &._mobile._company {
    flex-wrap: wrap;
    flex-direction: initial;
  }

  &._mobile &__verification-icon {
    margin-left: 2px;
  }
}
</style>
